import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';

import { Button, ModalV2 as Modal, truncateTextWithEllipsis } from '@utilities';

import QuestionLogicCondition from './components/QuestionLogicCondition';
import {
  getNonInstructionQuestionCountBefore,
  getQuestionsCost,
} from '../../../../../../utilities/helpers';
import { getPipingQuestionNumbersById, replacePipingMentionReference } from '../../../../utilities';

import styles from './_index.module.scss';

const ModalLogicChangeConfirmation = ({
  calculateQuestionNumbers,
  onClose,
  pipingQuestionsMap,
  questionBeingMoved,
  questionLogic,
  questions,
  updateSurvey,
}) => {
  const { permissions = {} } = useSelector((state) => state?.user?.data) || {};
  const { canAdvancedEditSurvey } = permissions;

  const applyChanges = () => {
    let updatedQuestions = [...questions];

    // Remove affected children questions from their current positions
    const affectedChildrenIds = affectedChildrenQuestions.map((q) => q.id);
    updatedQuestions = updatedQuestions.filter((q) => !affectedChildrenIds.includes(q.id));

    const newMovedQuestionIndex = updatedQuestions.findIndex((q) => q.id === questionBeingMoved.id);

    // Insert children right after the moved question
    updatedQuestions.splice(newMovedQuestionIndex + 1, 0, ...affectedChildrenQuestions);

    // Recalculate question numbers for the new order
    const updatedQuestionNumbers = calculateQuestionNumbers(updatedQuestions);
    const updatedPipingQuestionNumbers = getPipingQuestionNumbersById(updatedQuestions);

    const oldParentCanonicalQuestionNumber =
      pipingQuestionNumbersByQuestions[questionBeingMoved.id];
    const newParentCanonicalQuestionNumber = updatedPipingQuestionNumbers[questionBeingMoved.id];
    const oldParentDisplayQuestionNumber = questionNumbersByQuestions[questionBeingMoved.id];
    const newParentDisplayQuestionNumber = updatedQuestionNumbers[questionBeingMoved.id];

    updatedQuestions = updatedQuestions.map((question) => {
      if (!affectedChildrenIds.includes(question.id)) {
        return question;
      }

      return {
        ...question,
        richText: replacePipingMentionReference({
          newCanonicalQuestionNumber: newParentCanonicalQuestionNumber,
          newDisplayQuestionNumber: newParentDisplayQuestionNumber,
          oldCanonicalQuestionNumber: oldParentCanonicalQuestionNumber,
          oldDisplayQuestionNumber: oldParentDisplayQuestionNumber,
          value: question.richText,
        }),
        text: replacePipingMentionReference({
          newCanonicalQuestionNumber: newParentCanonicalQuestionNumber,
          newDisplayQuestionNumber: newParentDisplayQuestionNumber,
          oldCanonicalQuestionNumber: oldParentCanonicalQuestionNumber,
          oldDisplayQuestionNumber: oldParentDisplayQuestionNumber,
          value: question.text,
        }),
      };
    });

    updateSurvey(
      { questionLogic: proposedQuestionLogic, questions: updatedQuestions },
      { markDirty: true }
    );
    onClose();
  };

  const affectedDisqualifyQuestions = questions.filter(
    ({ id }, index) =>
      !canAdvancedEditSurvey &&
      getNonInstructionQuestionCountBefore(questions, index) >= 2 &&
      questionLogic[id]?.disqualify
  );

  // Calculate question numbers for current order
  const questionNumbersByQuestions = calculateQuestionNumbers(questions);
  const pipingQuestionNumbersByQuestions = getPipingQuestionNumbersById(questions);

  const parentIds = (pipingQuestionsMap.parentsByChild[questionBeingMoved.id] ?? []).map((parent) =>
    String(parent.questionId)
  );

  const childrenIds = (pipingQuestionsMap.childrenByParent[questionBeingMoved.id] ?? []).map(
    (child) => String(child.questionId)
  );

  // If the moving question is a parent in the piping relation, find all children questions that are before the moving question
  const affectedChildrenQuestions = questions.filter(({ id }) => {
    return (
      childrenIds.includes(String(id)) &&
      pipingQuestionNumbersByQuestions[id] < pipingQuestionNumbersByQuestions[questionBeingMoved.id]
    );
  });

  // If the moving question is a child in the piping relation, find all parent questions that are after the moving question
  const affectedParentQuestions = questions.filter(({ id }) => {
    return (
      parentIds.includes(String(id)) &&
      pipingQuestionNumbersByQuestions[id] > pipingQuestionNumbersByQuestions[questionBeingMoved.id]
    );
  });

  const affectedOptionLogicQuestions = questions.filter(({ id }, index) => {
    const currentLogic = questionLogic[id];
    if (!currentLogic?.option) return false;

    if (
      Object.values(currentLogic.option).some((optionLogic) =>
        optionLogic?.logic.some(
          ({ question }) =>
            question &&
            questions.findIndex(({ id: questionId }) => question.id === questionId) > index
        )
      )
    ) {
      return true;
    }

    return false;
  });

  const affectedSkipQuestions = questions.filter(({ id }, index) => {
    const currentLogic = questionLogic[id];
    if (!currentLogic?.skip) return false;

    if (
      currentLogic?.skip?.logic?.some(
        ({ question }) =>
          question &&
          questions.findIndex(({ id: questionId }) => question.id === questionId) > index
      )
    ) {
      return true;
    }

    return false;
  });

  const destinationIndex = questions.findIndex(({ id }) => id === questionBeingMoved.id);
  const destinationCost = getQuestionsCost(questions.slice(0, destinationIndex)) + 1;

  const affectedOptionsByQuestion = affectedOptionLogicQuestions.reduce((acc, question) => {
    const questionIndex = questions.findIndex((q) => q.id === question.id);
    const affectedAnswers = [];

    Object.entries(questionLogic[question.id]?.option || {}).forEach(([answerId, optionLogic]) => {
      const answer = question.answers.find((ans) => String(ans.id) === answerId);
      const hasInvalidLogic = optionLogic?.logic?.some(
        ({ question: logicQuestion }) =>
          logicQuestion && questions.findIndex(({ id }) => id === logicQuestion.id) > questionIndex
      );

      if (hasInvalidLogic && answer) {
        affectedAnswers.push(answer);
      }
    });

    if (affectedAnswers.length > 0) {
      acc[question.id] = affectedAnswers;
    }
    return acc;
  }, {});

  const proposedQuestionLogic = cloneDeep(questionLogic);

  affectedDisqualifyQuestions.forEach((question) => {
    if (!proposedQuestionLogic[question.id]?.skip) {
      delete proposedQuestionLogic[question.id];
    } else {
      delete proposedQuestionLogic[question.id].disqualify;
    }
  });
  affectedSkipQuestions.forEach((question) => {
    // Only keep quota group logic and question logic that with questions above the current question
    proposedQuestionLogic[question.id].skip.logic = proposedQuestionLogic[
      question.id
    ].skip.logic.filter(
      (logic) =>
        !logic.question || questions.findIndex((q) => q.id === logic.question.id) < destinationIndex
    );
  });

  affectedOptionLogicQuestions.forEach((question) => {
    const questionIndex = questions.findIndex((q) => q.id === question.id);
    // Only keep quota group logic and question logic with questions above the current question
    Object.keys(proposedQuestionLogic[question.id].option).forEach((answerKey) => {
      const optionLogicForAnswer = proposedQuestionLogic[question.id].option[answerKey];

      if (!optionLogicForAnswer.logic) return;

      optionLogicForAnswer.logic = optionLogicForAnswer.logic.filter(
        (logic) =>
          !logic.question || questions.findIndex((q) => q.id === logic.question.id) < questionIndex
      );

      // Clean up empty option logic
      if (optionLogicForAnswer.logic.length === 0) {
        delete proposedQuestionLogic[question.id].option[answerKey];
      }
    });

    // Clean up empty option object
    if (Object.keys(proposedQuestionLogic[question.id].option).length === 0) {
      delete proposedQuestionLogic[question.id].option;
    }
  });

  const actionButtons = [
    <Button key="modal-cancel-button" onClick={onClose} text="Cancel" variant="secondary" />,
    <Button
      isDisabled={affectedParentQuestions.length}
      key="modal-confirm-button"
      onClick={applyChanges}
      text="Save"
    />,
  ];

  return (
    <Modal
      buttons={actionButtons}
      id="modal-logic-change-confirmation"
      isActive
      onClose={onClose}
      size="large"
      title="Confirm Changes"
    >
      <div className={styles['modal-logic-change-confirmation-body']}>
        <p>
          Are you sure you want to move the question to <strong>position {destinationCost}</strong>?
        </p>
        {affectedOptionLogicQuestions.map((question) => (
          <Fragment key={`affected-logic-${question.id}`}>
            <p>
              The following answer option logic for{' '}
              <strong>
                &quot;{truncateTextWithEllipsis({ length: 30, text: question.text })}
                &quot;
              </strong>{' '}
              will be removed as it will invalidate your survey logic:
            </p>
            {affectedOptionsByQuestion[question.id]?.map((answer) => (
              <QuestionLogicCondition
                answerId={answer.id}
                key={`option-logic-${answer.id}`}
                question={question}
                questionLogic={questionLogic}
                selectedQuestions={questions}
                type="option"
              />
            ))}
          </Fragment>
        ))}
        {affectedSkipQuestions.map((question) => (
          <Fragment key={`affected-logic-${question.id}`}>
            <p>
              The following skip logic for{' '}
              <strong>
                &quot;{truncateTextWithEllipsis({ length: 30, text: question.text })}
                &quot;
              </strong>{' '}
              will be removed as it will invalidate your survey logic:
            </p>
            <QuestionLogicCondition
              question={question}
              questionLogic={questionLogic}
              selectedQuestions={questions}
              type="skip"
            />
          </Fragment>
        ))}
        {affectedDisqualifyQuestions.map((question) => (
          <Fragment key={`affected-logic-${question.id}`}>
            <p>
              The following for{' '}
              <strong>
                &quot;{truncateTextWithEllipsis({ length: 30, text: question.text })}
                &quot;
              </strong>{' '}
              will be removed as disqualification can only be applied at position 1 or 2 in your
              questionnaire:
            </p>
            <QuestionLogicCondition
              question={question}
              questionLogic={questionLogic}
              type="disqualify"
            />
          </Fragment>
        ))}
        {affectedChildrenQuestions.length > 0 && (
          <>
            {affectedChildrenQuestions.map((question) => (
              <Fragment key={`affected-logic-${question.id}`}>
                <p>
                  It will affect this question{' '}
                  <strong>
                    &quot;
                    {truncateTextWithEllipsis({ length: 100, text: question.text })}
                    &quot;
                  </strong>{' '}
                  which contains piping answer from the moved question.
                </p>
              </Fragment>
            ))}
            <strong>
              These impacted question(s) contain piping answers from the moved question will be
              moved after the moved question.
            </strong>
          </>
        )}
        {affectedParentQuestions.map((question) => (
          <Fragment key={`affected-logic-${question.id}`}>
            <p>
              It will affect this question{' '}
              <strong>“{truncateTextWithEllipsis({ length: 100, text: question.text })}”</strong>{' '}
              which is the parent question from the moved question.
            </p>
          </Fragment>
        ))}
      </div>
    </Modal>
  );
};

ModalLogicChangeConfirmation.propTypes = {
  calculateQuestionNumbers: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  pipingQuestionsMap: PropTypes.object.isRequired,
  questionBeingMoved: PropTypes.object.isRequired,
  questionLogic: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  updateSurvey: PropTypes.func.isRequired,
};

export default ModalLogicChangeConfirmation;
